import { Result } from '@blade/std-lib'

import { PathfinderWsError } from './pathfinder-ws-client'

import type {
  PathfinderQuoteListener,
  PathfinderQuoteSubscription,
  PathfinderWsClient,
  PathfinderWsConnectionState,
  SubscribeParameters,
} from './pathfinder-ws-client'

/**
 * The pathfinder client for an environment that has no pathfinder.
 *
 * Not every deployment runs one: blade does not yet, and a laptop running the
 * API and nothing else certainly does not. The real client throws when it is
 * built without a URL, and it is built while the application starts — so a
 * missing pathfinder used to stop the terminal at its splash screen with an
 * uncaught error, which reads as "the app is broken" rather than as "that
 * service is not here".
 *
 * This one starts, stays disconnected, and refuses a quote with a sentence
 * that says why. The screens that ask for quotes already check
 * `pathfinderEnabled` first, so nothing reaches this in normal use; it exists
 * so that the ones that do not check fail in a way somebody can read.
 */
export const createDisabledPathfinderWsClient = (): PathfinderWsClient => {
  const state: PathfinderWsConnectionState = { kind: 'idle' }
  const refuse = () =>
    new PathfinderWsError('pathfinder is not configured in this environment')

  return {
    get state() {
      return state
    },
    onStateChange: () => () => {},
    subscribeQuote: async (
      _params: SubscribeParameters,
      _listener: PathfinderQuoteListener,
    ): Promise<Result.Result<PathfinderQuoteSubscription, PathfinderWsError>> =>
      Result.fail(refuse()),
    ping: async () => Result.fail(refuse()),
    dispose: () => {},
    openSocket: () => {},
  }
}
